import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, mergeProps as _mergeProps, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, withCtx as _withCtx, vShow as _vShow, withDirectives as _withDirectives, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "grid grid-cols max-w-[1144px] max-w-176 w-full items-start self-stretch" }
const _hoisted_2 = {
  key: 0,
  class: "racq-max-w-xl5"
}
const _hoisted_3 = {
  key: 0,
  class: "mt-12"
}
const _hoisted_4 = {
  key: 1,
  class: "max-w-176 mt-4"
}
const _hoisted_5 = {
  class: "mt-12",
  "data-test-id": "policy_summary_component"
}
const _hoisted_6 = { class: "flex md:w-176 items-start mt-4" }

import { computedAsync } from '@vueuse/core';
import { computed, onMounted, ref } from 'vue';
import { InsurancePolicy, ISitecoreFormGenericInput } from '@/interfaces';
import { useI18n } from 'vue-i18n';
import FormStepInputs from '@/components/FormStepInputs/FormStepInputs.vue';
import { useAccountStore } from '@/store';
import { getFormInput, getFormInputByName } from '@/utils/form-input-helper';
import PanelTitle from '@/components/PanelTitle/PanelTitle.vue';
import { SelfServiceService } from '@/services/store/self-service-service';
import Skeleton from '@/components/Skeleton/Skeleton.vue';
import LinkPanel from '@/components/LinkPanel/LinkPanel.vue';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import { InformationBoxTypes } from '@/interfaces/forms/information-box.interface';
import { getPolicyStatusDetails } from '@/utils/self-service-rules/InsurancePolicyStatusRules';
import { getActionBarDisplayDetails } from '@/utils/self-service-rules/InsuranceActionBarRules';
import { isMobileDevice } from '@/utils/dom-helpers';
import { getCurrentPolicy } from './ProductDetailsHelper';
import { SelfServiceSessionPages } from '@/enums/self-service-session-pages';
import PolicySummary from './PolicySummary.vue';
import ProductDetailsPaymentInformation from './ProductDetailsPaymentInformation.vue';
import ClaimsPanel from '@/components/ClaimsPanel/ClaimsPanel.vue';
import StatusMessages from './StatusMessages/StatusMessages.vue';
import ProductDetailsPolicyDetails from './ProductDetailsPolicyDetails.vue';
import {
  DISABLE_INSURANCE_CANCEL_POLICY_BUTTON,
  ENABLE_CONTACT_US_INSURANCE_CANCELLATION,
} from '@/constants/feature-toggles';
import { useFeatureTogglesStore } from '../../../../store/feature-toggles/featureTogglesStore';
import ModalDialog from '@/components/ModalDialog/ModalDialog.vue';
import SelectCoverageOption from '@/components/SelectCoverageOption/SelectCoverageOption.vue';
import { PageAnalyticsBuilder } from '@/utils/page-analytics-builder';
import { FormEvents } from '@/enums/form-events.enum';
import { PaymentMethod } from '@/enums/payment-types.enum';

enum AdjustYourCoverItems {
  switchToMonthlyPayments = 'switchToMonthlyPayments',
  addOptionalBenefits = 'addOptionalBenefits',
  keepYourPropertyDetailsUpToDate = 'keepYourPropertyDetailsUpToDate',
  hasSomethingChanged = 'hasSomethingChanged',
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ProductDetails',
  props: {
    content: {},
    id: {},
    name: {},
    testPolicyNumber: {}
  },
  setup(__props: any) {

const translation = useI18n();
const dictionary: Record<string, string> = translation.getLocaleMessage('en') as any;

const accountStore = useAccountStore();
const featureTogglesStore = useFeatureTogglesStore();

const props = __props;

const currentPolicy = ref<InsurancePolicy>();
const adjustYourCoverLinks = ref<any>(getFormInput(props.content, 'adjustYourCoverLinks'));
const claimsIncomplete = ref<any>(getFormInputByName(props.content, 'claimsIncomplete'));
const cancelPolicy = ref<any>(getFormInput(props.content, 'cancelPolicy'));
const cancelPolicyContact = ref<any>(getFormInput(props.content, 'cancelPolicyContact'));
const cancelCoverDisabledInfoBox = ref<any>(getFormInput(props.content, 'cancelCoverDisabledInfoBox'));
const actionBarRuleResult = ref<any>();
const currentPolicyLoaded = ref<boolean>(false);

const policySummary = ref<any>();
const isLoading = ref<boolean>(props?.testPolicyNumber ? false : true);
const selfServiceToggles = ref<any>();
const enableContactUsInsuranceCancellation = ref(false);
const disableInsuranceCancelPolicyButton = ref(false);
const hasIncompleteClaims = ref(false);

const productIcons = ref<any>(getFormInputByName(props.content, 'productIcons'));
const selectPropertyModal = ref<any>({
  content: getFormInputByName(props.content, 'selectProperty'),
  model: '',
  modalIsOpen: false,
  linkItem: null,
});

onMounted(async () => {
  const queryString = window.location.search.toLowerCase();
  const urlParams = new URLSearchParams(queryString);
  const policyNumber = urlParams.get('policynumber');

  if (policyNumber) {
    await accountStore.fetchIdentityDetails(true);
  }

  if (accountStore?.identityDetails?.portfolio?.insurancePolicies) {
    currentPolicy.value = getCurrentPolicy(
      accountStore.identityDetails?.portfolio?.insurancePolicies,
      props.testPolicyNumber
    );
    currentPolicyLoaded.value = true;
  }

  enableContactUsInsuranceCancellation.value = await featureTogglesStore.isFeatureToggleEnabled(
    ENABLE_CONTACT_US_INSURANCE_CANCELLATION
  );

  disableInsuranceCancelPolicyButton.value = await featureTogglesStore.isFeatureToggleEnabled(
    DISABLE_INSURANCE_CANCEL_POLICY_BUTTON
  );

  policySummary.value = getPolicySummaryModel();

  pushAnalytics();
});

function getPolicySummaryModel(): any {
  const tempPolicySummary = getFormInput(props.content, 'policySummary');
  if (!tempPolicySummary) return { title: '', icon: '', items: [] };
  return {
    title: tempPolicySummary.title,
    icon: tempPolicySummary.fields[0].icon,
    items: tempPolicySummary.fields[0].items,
  };
}

const handleCancelInsuranceLink = (cancelPolicyFields: any) => {
  const excludeName = enableContactUsInsuranceCancellation.value ? 'cancel' : 'contactUs';
  return cancelPolicyFields.filter((x: any) => x.name !== excludeName);
};

const hasIncompleteClaimsEmit = (response: boolean) => {
  hasIncompleteClaims.value = response;
};

const policyStatusRules = computedAsync(async () => {
  if (currentPolicy.value) {
    let renewalPolicy = undefined;
    if (currentPolicy.value?.state?.isInRenewal == true) {
      renewalPolicy = await accountStore.fetchPolicyRenewal(currentPolicy.value.policyNumber);
    }

    return await getPolicyStatusDetails(currentPolicy.value, new Date(), false, renewalPolicy);
  }
});

const isPolicyCancelDisabled = computed(() => {
  if (!currentPolicy.value) return true;

  return (
    !currentPolicy.value.state.isPolicyCancelAllowed ||
    disableInsuranceCancelPolicyButton.value ||
    currentPolicy.value?.cancellationDate !== undefined
  );
});

const filteredAdjustYourCoverLinks = computedAsync(async () => {
  if (!currentPolicy?.value && accountStore?.identityDetails?.portfolio?.insurancePolicies) {
    currentPolicy.value = getCurrentPolicy(
      accountStore.identityDetails.portfolio.insurancePolicies,
      props.testPolicyNumber
    );
  }

  try {
    if (currentPolicy.value) {
      selfServiceToggles.value = await SelfServiceService.getSelfServiceToggles();
      const tempAdjustYourCoverLinks = getFormInput(props.content, 'adjustYourCoverLinks');
      const validItems = [] as AdjustYourCoverItems[];

      let renewalPolicy = undefined;
      if (currentPolicy.value?.state?.isInRenewal == true) {
        renewalPolicy = await accountStore.fetchPolicyRenewal(currentPolicy.value.policyNumber);
      }

      const rulesResult = await getPolicyStatusDetails(currentPolicy.value, new Date(), true, renewalPolicy);

      actionBarRuleResult.value = await getActionBarDisplayDetails(currentPolicy.value, selfServiceToggles.value);

      if (actionBarRuleResult.value.showSwitchToMonthly) {
        validItems.push(AdjustYourCoverItems.switchToMonthlyPayments);
      }

      if (selfServiceToggles.value.enableViewBenefitsButton && rulesResult.showAddToCover) {
        validItems.push(AdjustYourCoverItems.addOptionalBenefits);
      }

      if (actionBarRuleResult.value.showUpdatePropertyDetails) {
        validItems.push(AdjustYourCoverItems.keepYourPropertyDetailsUpToDate);
      }

      validItems.push(AdjustYourCoverItems.hasSomethingChanged);

      const tempFilteredFields = tempAdjustYourCoverLinks;

      tempFilteredFields.fields = tempAdjustYourCoverLinks.fields.filter((x: any) => validItems.includes(x.name));

      isLoading.value = false;
      return tempFilteredFields;
    }
  } catch (error) {
    console.error('error', error);
  }

  return null;
});

const onClick_AdjustYourCoverButton = async (linkItem: any) => {
  if (!currentPolicy?.value) return;

  let redirectUrl;
  let response;
  let coverageId;

  switch (linkItem.name) {
    case AdjustYourCoverItems.switchToMonthlyPayments:
      response = await SelfServiceService.createQuoteSession(
        currentPolicy.value.policyNumber,
        SelfServiceSessionPages.ChangeMonthlyPaymentPage
      );
      if (response?.success) {
        redirectUrl = `${linkItem?.link?.value?.href}${linkItem?.link?.value?.querystring.replace('{0}', response.data)}`;
        window.location.href = redirectUrl;
      }
      break;
    case AdjustYourCoverItems.addOptionalBenefits:
      redirectUrl = `${linkItem?.link?.value?.href.replace('{0}', currentPolicy.value.policyNumber)}`;
      window.location.href = redirectUrl;
      break;
    case AdjustYourCoverItems.keepYourPropertyDetailsUpToDate:
      if (currentPolicy.value.coverages.length > 1) {
        await pushModalAnalytics();

        selectPropertyModal.value.linkItem = linkItem;
        selectPropertyModal.value.modalIsOpen = true;
      } else {
        coverageId = currentPolicy.value.coverages[0].id;
        handleUpdatePropertyDetailsRedirect(coverageId, linkItem);
      }

      break;
    case AdjustYourCoverItems.hasSomethingChanged:
      if (isMobileDevice()) {
        window.location.href = linkItem.link.value.href;
      }
      break;
  }
};

function updateTelephoneLinks(fields: any[]) {
  fields.forEach((field) => {
    field.link.value.href =
      field.link.value.href.indexOf('tel:') > -1
        ? field.link.value.href.replace('https://', '').replace('http://', '')
        : field.link.value.href;
  });
  return fields;
}

const handleUpdatePropertyDetailsRedirect = (coverageId: string, linkItem: any) => {
  if (!currentPolicy.value) return;
  const redirectUrl = `${linkItem?.link?.value?.href?.replace('{0}', currentPolicy.value.policyNumber)?.replace('{1}', coverageId)}`;
  window.location.href = redirectUrl;
};

const selectPropertyModal_Next = (value: boolean) => {
  selectPropertyModal.value.modalIsOpen = false;

  const coverageId = selectPropertyModal.value.model;
  const coverage = currentPolicy.value?.coverages.find((x) => x.id === coverageId);

  if (coverage) {
    handleUpdatePropertyDetailsRedirect(coverageId, selectPropertyModal.value.linkItem);
  }
};

function selectPropertyModal_Close(e: any) {
  selectPropertyModal.value.modalIsOpen = false;
}

const pushAnalytics = async () => {
  let currentPolicyPaymentMethods = null;
  let currentTerm = 0;
  if (currentPolicy.value?.state.isInRenewal) {
    currentTerm = currentPolicy.value!.renewalTermPaymentDetails?.termNumber;
  } else {
    currentTerm = currentPolicy.value!.currentTermPaymentDetails?.termNumber;
  }

  if (currentTerm) {
    try {
      currentPolicyPaymentMethods = await SelfServiceService.getPaymentMethods(
        currentPolicy.value!.policyNumber!,
        currentTerm
      );
    } catch (error) {
      console.error(error);
    }
  }

  const paymentMethod =
    PaymentMethod.BankAccount === currentPolicyPaymentMethods?.paymentMethod &&
    currentPolicyPaymentMethods.bankAccountDetails !== null
      ? 'Bank account'
      : currentPolicyPaymentMethods?.paymentMethod === PaymentMethod.CreditCard &&
          currentPolicyPaymentMethods.creditCardDetails !== null
        ? 'Credit card'
        : 'Unknown';

  const pageAnalytics = new PageAnalyticsBuilder('Insurance Product Details');
  pageAnalytics.populateProducts(dictionary, policyStatusRules.value, currentPolicy.value?.policyNumber, paymentMethod);
  pageAnalytics.pushAnalytics();
};

const pushModalAnalytics = async () => {
  if (!currentPolicy?.value) return;

  const isAuthenticated = await accountStore.isAuthenticated;
  const pageAnalytics = new PageAnalyticsBuilder('Insurance Product Details');
  pageAnalytics.populateModalStart(
    FormEvents.START,
    isAuthenticated,
    'Insurance Product Details',
    'Combined policy property update modal',
    currentPolicy.value.termEnd ?? undefined,
    accountStore.identityDetails?.d365Id
  );
  pageAnalytics.pushAnalytics();
};

const skeletonBlocks = [
  {
    height: 26,
    width: 338,
    class: 'mb-3',
  },
  {
    height: 26,
    width: 176,
    class: 'mb-3',
  },
  {
    height: 88,
    width: 88,
    class: 'mb-3',
  },
  {
    height: 36,
    width: 244,
    class: 'mb-2',
  },
  {
    height: 36,
    width: 244,
    class: 'mb-6',
  },
  {
    height: 48,
    width: 204,
    class: 'mb-6',
  },
  {
    height: 26,
    width: 204,
    class: 'mb-5',
  },
];

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      (!isLoading.value && currentPolicy.value?.policyNumber?.length == 0)
        ? (_openBlock(), _createBlock(InformationBox, {
            key: 0,
            description: "Policy not found",
            type: _unref(InformationBoxTypes).ERROR
          }, null, 8, ["type"]))
        : _createCommentVNode("", true),
      (!isLoading.value && currentPolicy.value?.policyNumber)
        ? (_openBlock(), _createBlock(FormStepInputs, {
            key: 1,
            step: {
        stepName: _ctx.name,
        formInputs: _ctx.content,
      }
          }, {
            claimsIncomplete: _withCtx(() => [
              _createElementVNode("div", null, [
                _createVNode(ClaimsPanel, {
                  fields: claimsIncomplete.value,
                  title: claimsIncomplete.value.title,
                  subtitle: claimsIncomplete.value.subtitle,
                  brief: claimsIncomplete.value.brief,
                  claimRef: claimsIncomplete.value.claimRef,
                  claimDetails: claimsIncomplete.value.claimDetails,
                  expiryDetails: claimsIncomplete.value.expiryDetails,
                  resumeBtn: claimsIncomplete.value.resumeBtn,
                  onHasIncompleteClaims: _cache[0] || (_cache[0] = (e) => hasIncompleteClaimsEmit(e))
                }, null, 8, ["fields", "title", "subtitle", "brief", "claimRef", "claimDetails", "expiryDetails", "resumeBtn"])
              ])
            ]),
            manageYourPolicy: _withCtx(() => [
              _createElementVNode("div", {
                class: _normalizeClass({ 'mt-12': hasIncompleteClaims.value })
              }, [
                (!isLoading.value)
                  ? (_openBlock(), _createBlock(PanelTitle, _normalizeProps(_mergeProps({ key: 0 }, _unref(getFormInput)(props.content, 'manageYourPolicy'))), null, 16))
                  : _createCommentVNode("", true)
              ], 2),
              (_unref(policyStatusRules))
                ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                    _createVNode(StatusMessages, {
                      insurancePolicy: currentPolicy.value,
                      statusMessages: _unref(getFormInputByName)(props.content, 'statusMessages'),
                      selfServiceToggles: selfServiceToggles.value,
                      policyStatusRules: _unref(policyStatusRules),
                      renewLink: _unref(getFormInput)(props.content, 'renewLink')
                    }, null, 8, ["insurancePolicy", "statusMessages", "selfServiceToggles", "policyStatusRules", "renewLink"])
                  ]))
                : _createCommentVNode("", true)
            ]),
            adjustYourCoverLinks: _withCtx(() => [
              (_unref(filteredAdjustYourCoverLinks)?.fields?.length > 0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                    _createVNode(LinkPanel, {
                      fields: _unref(filteredAdjustYourCoverLinks),
                      heading: adjustYourCoverLinks.value.heading,
                      headingDescription: adjustYourCoverLinks.value.headingDescription,
                      subheading: adjustYourCoverLinks.value.subheading,
                      subheadingDescription: adjustYourCoverLinks.value.subheadingDescription,
                      links: updateTelephoneLinks(adjustYourCoverLinks.value.fields),
                      isGreyBackground: adjustYourCoverLinks.value.isGreyBackground,
                      isArrowLinks: adjustYourCoverLinks.value.isArrowLinks,
                      iconSize: adjustYourCoverLinks.value.iconSize,
                      globalIcon: adjustYourCoverLinks.value.globalIcon,
                      globalButtonLabel: adjustYourCoverLinks.value.globalButtonLabel,
                      handleLinkClickEvent: true,
                      onBtnClick: _cache[1] || (_cache[1] = (linkName) => onClick_AdjustYourCoverButton(linkName))
                    }, null, 8, ["fields", "heading", "headingDescription", "subheading", "subheadingDescription", "links", "isGreyBackground", "isArrowLinks", "iconSize", "globalIcon", "globalButtonLabel"])
                  ]))
                : _createCommentVNode("", true),
              (
            !_unref(filteredAdjustYourCoverLinks) || _unref(filteredAdjustYourCoverLinks)?.fields?.length == 0 || !currentPolicyLoaded.value
          )
                ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                    _createVNode(Skeleton, { blocks: skeletonBlocks })
                  ]))
                : _createCommentVNode("", true)
            ]),
            policySummary: _withCtx(() => [
              _createElementVNode("div", _hoisted_5, [
                (currentPolicyLoaded.value)
                  ? (_openBlock(), _createBlock(PolicySummary, {
                      key: 0,
                      currentPolicy: currentPolicy.value,
                      content: props.content
                    }, null, 8, ["currentPolicy", "content"]))
                  : _createCommentVNode("", true)
              ])
            ]),
            policyDetails: _withCtx(() => [
              _createElementVNode("div", null, [
                (currentPolicyLoaded.value)
                  ? (_openBlock(), _createBlock(ProductDetailsPolicyDetails, {
                      key: 0,
                      content: _ctx.content,
                      currentPolicyTemp: currentPolicy.value,
                      selfServiceToggles: selfServiceToggles.value
                    }, null, 8, ["content", "currentPolicyTemp", "selfServiceToggles"]))
                  : _createCommentVNode("", true)
              ])
            ]),
            paymentInformation: _withCtx(() => [
              (currentPolicyLoaded.value)
                ? (_openBlock(), _createBlock(ProductDetailsPaymentInformation, {
                    key: 0,
                    id: "paymentInformation",
                    content: _ctx.content,
                    currentPolicyTemp: currentPolicy.value,
                    selfServiceToggles: selfServiceToggles.value,
                    actionBarRuleResult: actionBarRuleResult.value
                  }, null, 8, ["content", "currentPolicyTemp", "selfServiceToggles", "actionBarRuleResult"]))
                : _createCommentVNode("", true)
            ]),
            cancelPolicyContact: _withCtx(() => [
              _createElementVNode("div", null, [
                (
              currentPolicy.value.state.isPolicyCancelAllowed &&
              !disableInsuranceCancelPolicyButton.value &&
              !currentPolicy.value.cancellationDate &&
              enableContactUsInsuranceCancellation.value
            )
                  ? (_openBlock(), _createBlock(LinkPanel, {
                      key: 0,
                      class: "mt-12",
                      heading: cancelPolicyContact.value.heading,
                      headingDescription: cancelPolicyContact.value.headingDescription,
                      subheading: cancelPolicyContact.value.subheading,
                      subheadingDescription: cancelPolicyContact.value.subheadingDescription,
                      links: cancelPolicyContact.value.fields,
                      isGreyBackground: cancelPolicyContact.value.isGreyBackground,
                      isArrowLinks: cancelPolicyContact.value.isArrowLinks,
                      iconSize: cancelPolicyContact.value.iconSize,
                      globalIcon: cancelPolicyContact.value.globalIcon,
                      globalButtonLabel: cancelPolicyContact.value.globalButtonLabel
                    }, null, 8, ["heading", "headingDescription", "subheading", "subheadingDescription", "links", "isGreyBackground", "isArrowLinks", "iconSize", "globalIcon", "globalButtonLabel"]))
                  : _createCommentVNode("", true)
              ])
            ]),
            cancelPolicy: _withCtx(() => [
              _createElementVNode("div", null, [
                _createVNode(LinkPanel, {
                  disabledPanel: isPolicyCancelDisabled.value,
                  class: "mt-12",
                  heading: cancelPolicy.value.heading,
                  headingDescription: cancelPolicy.value.headingDescription,
                  subheading: cancelPolicy.value.subheading,
                  subheadingDescription: cancelPolicy.value.subheadingDescription,
                  links: handleCancelInsuranceLink(cancelPolicy.value.fields),
                  isGreyBackground: cancelPolicy.value.isGreyBackground,
                  isArrowLinks: cancelPolicy.value.isArrowLinks,
                  iconSize: cancelPolicy.value.iconSize,
                  globalIcon: cancelPolicy.value.globalIcon,
                  globalButtonLabel: cancelPolicy.value.globalButtonLabel
                }, null, 8, ["disabledPanel", "heading", "headingDescription", "subheading", "subheadingDescription", "links", "isGreyBackground", "isArrowLinks", "iconSize", "globalIcon", "globalButtonLabel"])
              ])
            ]),
            cancelCoverDisabledInfoBox: _withCtx(() => [
              _createElementVNode("div", _hoisted_6, [
                (isPolicyCancelDisabled.value)
                  ? (_openBlock(), _createBlock(InformationBox, {
                      key: 0,
                      title: cancelCoverDisabledInfoBox.value.title.value,
                      description: cancelCoverDisabledInfoBox.value.content.value,
                      type: cancelCoverDisabledInfoBox.value.messageType.value,
                      isCircle: true,
                      useButtonSlot: false
                    }, null, 8, ["title", "description", "type"]))
                  : _createCommentVNode("", true)
              ])
            ]),
            _: 1
          }, 8, ["step"]))
        : _createCommentVNode("", true)
    ]),
    _createVNode(ModalDialog, {
      modelValue: selectPropertyModal.value.modalIsOpen,
      showXCloseButton: true,
      closeOnEsc: true,
      size: "md",
      "onUpdate:modelValue": selectPropertyModal_Close
    }, {
      default: _withCtx(() => [
        _createVNode(SelectCoverageOption, {
          modelValue: selectPropertyModal.value.model,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((selectPropertyModal.value.model) = $event)),
          coverages: currentPolicy.value?.coverages ?? [],
          fields: {
        title: selectPropertyModal.value?.content?.modalTitle,
        subtitle: selectPropertyModal.value?.content?.modalBody,
        homeIcon: productIcons.value?.homeIcon,
        homeContentsIcon: productIcons.value?.homeContentsIcon,
        contentsIcon: productIcons.value?.contentsIcon,
        motorIcon: productIcons.value?.motorIcon,
        motorThirdPartyIcon: productIcons.value?.motorThirdPartyIcon,
        motorThirdPartyFireIcon: productIcons.value?.motorThirdPartyFireIcon,
        boatIcon: productIcons.value?.boatIcon,
        caravanIcon: productIcons.value?.caravanIcon,
        motorcycleIcon: productIcons.value?.motorcycleIcon,
        petIcon: productIcons.value?.petIcon,
        modalBtnText: selectPropertyModal.value?.content?.callToActionButtonText,
      },
          onButtonClicked: selectPropertyModal_Next
        }, null, 8, ["modelValue", "coverages", "fields"])
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _withDirectives(_createElementVNode("div", null, [
      _createVNode(Skeleton, { blocks: skeletonBlocks })
    ], 512), [
      [_vShow, isLoading.value]
    ])
  ], 64))
}
}

})